import { removeFalsy } from "helpers/formatter";
import {
  TActiveMemberDemographicsReport,
  TAwardLevelReport,
  TEmailAddressReport,
  TInstructorRetentionMonthlyReport,
  TMemberRankingReport,
  TTCAAppointmentsCompletedReport,
  TTCAAppointmentsReport,
  TTCAGraduationsReport,
} from "models/report";
import apiService from "modules/api/apiService";
import {
  REPORTS_ACTIVE_MEMBER_DEMOGRAPHIC,
  REPORTS_AWARD_LEVEL_REPORT,
  REPORTS_CANCELLATION_REASON_REPORT,
  REPORTS_EMAIL_ADDRESS_REPORT,
  REPORTS_INSTRUCTOR_RETENTION_MONTHLY_REPORT,
  REPORTS_MEMBER_RANKING_FITPOINTS_BY_AGE_GROUP_REPORT,
  REPORTS_MEMBER_RANKING_REPORT,
  REPORTS_MEMBER_REPORT,
  REPORTS_MEMBER_RETENTION_DETAILED_REPORT,
  REPORTS_MEMBER_STATISTICS_REPORT,
  REPORTS_TCA_APPOINTMENTS,
  REPORTS_TCA_APPOINTMENTS_COMPLETED,
  REPORTS_TCA_GRADUATIONS,
} from "modules/api/endpoint";
import { TPagination } from "modules/api/type";

export const downloadReport = async (
  url: string,
  params: any,
  exportType: string
): Promise<any> => {
  params.export_as = exportType;

  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";

  try {
    const result = await apiService.get(`${url}${queryParams ? `?${queryParams}` : ""}`);

    let fileType = "";
    if (exportType === "PDF") fileType = "application/pdf";

    let file = new Blob([result.data], { type: fileType });
    window.open(URL.createObjectURL(file));
  } catch (error) {
    return { success: false };
  }
};

export const getAwardLevelReportsList = async (params: {
  award_level: string;
  gender: string;
  members_set: string;
  group_id: number;
  show_all_data: boolean;
  pdf: boolean;
  page: number;
  limit: number;
}): Promise<{
  success: boolean;
  data?: TAwardLevelReport[];
  pagination?: TPagination;
}> => {
  let queryParams = Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join("&");

  try {
    const results = await apiService.get(`${REPORTS_AWARD_LEVEL_REPORT}?${queryParams}`);
    return {
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getAMDemographicList = async (params: {
  group_id?: number;
  page?: number;
  limit?: number;
}): Promise<{
  success: boolean;
  data?: TActiveMemberDemographicsReport[];
}> => {
  let queryParams = params
    ? Object.keys(params)
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";
  try {
    const results = await apiService.get(
      REPORTS_ACTIVE_MEMBER_DEMOGRAPHIC + `${queryParams ? `?${queryParams}` : ""}`
    );

    return {
      data: results.data.payload.result,
      success: true,
    };
  } catch (error) {
    return {
      success: false,
    };
  }
};

export const getEmailReportsList = async (params: {
  view_by: string;
  members_status: string;
  sort_by: string;
  sort_order: string;
  page: number;
  limit: number;
}): Promise<{
  success: boolean;
  data?: TEmailAddressReport[];
  pagination?: TPagination;
}> => {
  let queryParams = Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join("&");

  try {
    const results = await apiService.get(`${REPORTS_EMAIL_ADDRESS_REPORT}?${queryParams}`);
    return {
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getCancellationReportsList = async (params: {
  view_by: string;
  sort_by: string;
  sort_order: string;
  page: number;
  limit: number;
}): Promise<{
  success: boolean;
  data?: TEmailAddressReport[];
  pagination?: TPagination;
}> => {
  let queryParams = Object.keys(removeFalsy(params))
    .map((key) => `${key}=${params[key]}`)
    .join("&");

  try {
    const results = await apiService.get(`${REPORTS_CANCELLATION_REASON_REPORT}?${queryParams}`);
    return {
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getMemberRankingReportsList = async (params: {
  valued_by: string;
  members_set: string;
  group_id?: string;
  name_ordering: string;
  age_group: string;
  gender: string;
  view_interval_options: string;
  date_from?: string;
  date_to?: string;
  page: number;
  limit: number;
}): Promise<{
  success: boolean;
  data?: TMemberRankingReport[];
  pagination?: TPagination;
}> => {
  let queryParams = Object.keys(removeFalsy(params))
    .filter((key) => params[key])
    .map((key) => `${key}=${params[key]}`)
    .join("&");

  try {
    const results = await apiService.get(`${REPORTS_MEMBER_RANKING_REPORT}?${queryParams}`);
    return {
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getMemberRankingFitpointsByAgeGroupReportsList = async (params: {
  gender: string;
  members_set: string;
  group_id?: string;
  view_interval_options: string;
  date_from?: string;
  date_to?: string;
}): Promise<{
  success: boolean;
  data?: TMemberRankingReport[];
  // pagination?: TPagination;
}> => {
  let queryParams = Object.keys(params)
    .filter((key) => params[key])
    .map((key) => `${key}=${params[key]}`)
    .join("&");

  try {
    const results = await apiService.get(
      `${REPORTS_MEMBER_RANKING_FITPOINTS_BY_AGE_GROUP_REPORT}?${queryParams}`
    );
    return {
      data: results.data.payload,
      success: true,
      // pagination: results.data.payload.pagination,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getInstructorMessageDetailsList = async (params: {
  page: number;
  limit: number;
}): Promise<{
  success: boolean;
  data?: any[];
  pagination?: TPagination;
}> => {
  // TODO: handle when api support

  // let queryParams = Object.keys(params)
  //   .map((key) => `${key}=${params[key]}`)
  //   .join("&");

  try {
    // const results = await apiService.get(
    //   `${INSTRUCTOR_MESSAGE_DETAIL}?${queryParams}`
    // );
    return {
      data: [],
      pagination: null,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getInstructorMessageStatsList = async (params: {
  page: number;
  limit: number;
}): Promise<{
  success: boolean;
  data?: any[];
  pagination?: TPagination;
}> => {
  // TODO: handle when api support

  // let queryParams = Object.keys(params)
  //   .map((key) => `${key}=${params[key]}`)
  //   .join("&");

  try {
    // const results = await apiService.get(
    //   `${INSTRUCTOR_MESSAGE_STATS}?${queryParams}`
    // );
    return {
      data: [],
      pagination: null,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getInstructorRetentionMonthlyReportList = async (params: {
  instructor_id: number;
  site: number;
  page: number;
  limit: number;
}): Promise<{
  success: boolean;
  data?: TInstructorRetentionMonthlyReport[];
  pagination?: TPagination;
}> => {
  let queryParams = Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join("&");

  try {
    const results = await apiService.get(
      `${REPORTS_INSTRUCTOR_RETENTION_MONTHLY_REPORT}?${queryParams}`
    );
    return {
      data: results.data.payload,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getMemberRetentionDetailed = async (params) => {
  let queryParams = Object.keys(removeFalsy(params))
    .map((key) => `${key}=${params[key]}`)
    .join("&");

  try {
    const results = await apiService.get(
      `${REPORTS_MEMBER_RETENTION_DETAILED_REPORT}?${queryParams}`
    );
    return {
      chart_data: results.data.payload.chart_data,
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getMemberReportList = async (params: {
  instructor_id: number;
  site: number;
  page: number;
  limit: number;
}): Promise<{
  success: boolean;
  data?: TInstructorRetentionMonthlyReport[];
  pagination?: TPagination;
}> => {
  let queryParams = Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join("&");

  try {
    const results = await apiService.get(`${REPORTS_MEMBER_REPORT}?${queryParams}`);
    return {
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getMemberStatistics = async (params: {
  page: number;
  limit: number;
}): Promise<{
  success: boolean;
  data?: any[];
  pagination?: TPagination;
}> => {
  //console.log(`params has ${JSON.stringify(params)}`);
  let queryParams = Object.keys(removeFalsy(params))
    .map((key) => `${key}=${params[key]}`)
    .join("&");

  try {
    //console.log(`queryParams are: ${JSON.stringify(queryParams)}`);
    const results = await apiService.get(`${REPORTS_MEMBER_STATISTICS_REPORT}?${queryParams}`);
    //console.log(`Results are: ${JSON.stringify(results.data)}`);
    return {
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    console.log("error ===>", e.message);
    return {
      success: false,
    };
  }
};

export const getTCAAppointmentsReport = async (params: {
  view_by: string;
  view_interval_options: string;
  status: string;
  sort_by: string;
  sort_order: string;
  group_id: number;
  page: number;
  limit?: number;
  date_from: string;
  date_thru: string;
  date_to: string;
}): Promise<{
  success: boolean;
  data?: TTCAAppointmentsReport[];
  pagination?: TPagination;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";
  try {
    const results = await apiService.get(`${REPORTS_TCA_APPOINTMENTS}?${queryParams}`);
    return {
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getTCAAppointmentsCompletedReport = async (params: {
  view_by: string;
  view_interval_options: string;
  status: string;
  sort_by: string;
  sort_order: string;
  group_id: number;
  page: number;
  limit?: number;
  date_from: string;
  date_thru: string;
  date_to: string;
}): Promise<{
  success: boolean;
  data?: TTCAAppointmentsCompletedReport[];
  pagination?: TPagination;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";
  try {
    const results = await apiService.get(`${REPORTS_TCA_APPOINTMENTS_COMPLETED}?${queryParams}`);
    //console.log(`results.data.payload.data has ${results.data.payload.data.length} items`);
    //console.log(`results are ${JSON.stringify(results)}`);
    return {
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};

export const getTCAGraduationsReport = async (params: {
  view_by: string;
  view_interval_options: string;
  status: string;
  sort_by: string;
  sort_order: string;
  group_id: number;
  page: number;
  limit: number;
  date_from: string;
  // date_thru: string;
  date_to: string;
}): Promise<{
  success: boolean;
  data?: TTCAGraduationsReport[];
  pagination?: TPagination;
}> => {
  let queryParams = params
    ? Object.keys(removeFalsy(params))
        .map((key) => `${key}=${params[key]}`)
        .join("&")
    : "";
  try {
    const results = await apiService.get(`${REPORTS_TCA_GRADUATIONS}?${queryParams}`);
    return {
      data: results.data.payload.data,
      pagination: results.data.payload.pagination,
      success: true,
    };
  } catch (e) {
    return {
      success: false,
    };
  }
};
